import { ActionIcon, Button, Group, Pagination, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { openModal } from '@mantine/modals';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getDatasetTrainingProgress, queryBotDatasetsList, useBotStore, useCollaborateStore } from '@mygpt/app';
import { SvgAdd } from '@/components/Common/icon';
import { DataFrozenTip } from '@/components/DataFrozenTip';
import { CloneModal } from '@/components/Datasets/CloneModal';
import { BindContent } from '@/components/Embeddings/BindContent';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import TableLoading from '@/components/TableLoading';
import { PageSizesInfo } from '@/types/interface';

import classes from './index.module.css';
import { EmbeddingsItem } from './item';
import { IconRefresh } from '@tabler/icons-react';

const MAX_CHECK_TIMES = 2;

export const Embeddings = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const matches = useMediaQuery('(min-width: 640px)');
  const { linkedDataset, setLinkedDataset, setDatasetStatus } = useBotStore();
  const { isAssist } = useCollaborateStore();
  const [refreshStatusLoading, setRefreshStatusLoading] = useState(false);

  // 因导航栏同步数据需要，暂时只查询第一页最大size，统一用store里的列表数据
  const [historyListPageInfo, setHistoryListPageInfo] = useState<PageSizesInfo>({
    page: 1,
    size: 1000,
    total: 0,
  });

  const checkCurrentTimes = useRef<number>(1);
  const [initList, setInitList] = useState(true);
  const listTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isEmptyList = useMemo(() => {
    return !linkedDataset.length && !initList;
  }, [linkedDataset, initList]);

  const paginationTotal = useMemo(() => {
    return Math.ceil(historyListPageInfo.total / historyListPageInfo.size);
  }, [historyListPageInfo]);

  const histroyListPageOnchange = (page: number) => {
    setHistoryListPageInfo({ ...historyListPageInfo, page });
  };

  const getHistoryList = async () => {
    if (!id) return;
    clearTimeout(listTimeoutRef.current); // 查看每页前清除自动轮询

    const res = await queryBotDatasetsList({
      aiId: id,
      page: historyListPageInfo.page,
      size: historyListPageInfo.size,
    });
    const { items, total } = res.data;
    setLinkedDataset(items);
    setHistoryListPageInfo((state) => ({ ...state, total }));

    if (!items.length && checkCurrentTimes.current < MAX_CHECK_TIMES) {
      checkCurrentTimes.current += 1;
      setTimeout(() => {
        getHistoryList();
      }, 3 * 1000);
      return;
    }

    setInitList(false);

    refreshStatus();
  };

  const refreshStatus = async () => {
    if (!id) return;
    if (refreshStatusLoading) return;
    setRefreshStatusLoading(true);

    try {
      const { data } = await getDatasetTrainingProgress({
        aiId: id,
        page: historyListPageInfo.page,
        size: historyListPageInfo.size,
      });
      setDatasetStatus(data.dataset_state);
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshStatusLoading(false);
    }
  };

  const refreshHistoryList = () => {
    const isCruuentPageLastItem = historyListPageInfo.page > 1 && linkedDataset.length === 1;
    if (isCruuentPageLastItem) {
      setHistoryListPageInfo({ ...historyListPageInfo, page: historyListPageInfo.page - 1 });
    } else {
      getHistoryList();
    }
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(listTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    getHistoryList();
  }, [historyListPageInfo.page]);

  const frozenTipOpened = useMemo(() => {
    return linkedDataset.some((dataset) => dataset.data_status === 'frozen');
  }, [linkedDataset]);

  const isCloning = useMemo(() => {
    return !!linkedDataset.some((item) => ['cloning_target', 'cloning_source'].includes(item.data_status));
  }, [linkedDataset]);

  useEffect(() => {
    if (!isCloning) return;

    const interval = setInterval(() => {
      getHistoryList();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [isCloning]);

  const openAddModal = () => {
    if (!id) return;
    openModal({
      title: t('embeddings.dataset.add_modal.desc'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: <BindContent id={id} initList={initList} onBind={getHistoryList} />,
    });
  };

  return (
    <>
      <ContainerHeader
        title={t('page.data_sources_title')}
        description={t('page.data_sources_description')}
        rightSection={
          !isAssist && (
            <Button
              className="group"
              onClick={() => {
                openAddModal();
              }}
              size={'sm'}
              leftSection={<SvgAdd />}
            >
              {t('embeddings.dataset.add')}
            </Button>
          )
        }
      />
      <div className={`${classes.page} flex flex-col h-full`}>
        {!linkedDataset.length && initList ? (
          <TableLoading />
        ) : (
          <section
            className={`flex flex-auto flex-col overflow-hidden relative rounded-[20px] bg-white border border-[#F8F6FF]`}
          >
            {isEmptyList && (
              <div className="h-[400px] flex items-center justify-center">
                <Stack align={'center'}>
                  <Text c="gray.5">{t('no_data')}</Text>
                </Stack>
              </div>
            )}
            {!!linkedDataset.length && (
              <>
                <DataFrozenTip visible={frozenTipOpened} />
                <Table className={classes.table} verticalSpacing="md" highlightOnHover captionSide="bottom">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ paddingLeft: '20px' }}>
                        <Group>
                          <span>{t('embeddings.table.name')}</span>
                        </Group>
                      </Table.Th>
                      <Table.Th className="w-[100px]">{t('embeddings.table.total_resources')}</Table.Th>
                      <Table.Th className="w-[150px]">
                        <Group gap={4}>
                          {t('embeddings.table.status')}
                          <Tooltip label={t('embeddings.table.refresh_status_tip')}>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="gray"
                              loading={refreshStatusLoading}
                              onClick={refreshStatus}
                            >
                              <IconRefresh size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Th>
                      <Table.Th className="hidden sm:block w-[200px]">{t('embeddings.table.upload_time')}</Table.Th>
                      {!isAssist && <Table.Th className="w-[90px]">{t('embeddings.table.action')}</Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {linkedDataset.map((item) => {
                      return <EmbeddingsItem key={item.id} item={item} handleUnbind={refreshHistoryList} />;
                    })}
                  </Table.Tbody>
                </Table>
                {paginationTotal > 1 && (
                  <section className="px-2 sm:px-8 mt-5">
                    <Pagination
                      size={matches ? 'md' : 'sm'}
                      onChange={histroyListPageOnchange}
                      total={paginationTotal}
                    />
                  </section>
                )}
              </>
            )}
          </section>
        )}
      </div>
      <CloneModal
        callback={(from) => {
          if (from === 'linked') getHistoryList();
        }}
        from="linked"
      />
    </>
  );
};
